"use client";

import { useCallback, useLayoutEffect, useRef, useState, useTransition } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useUser } from "@clerk/nextjs";
import { toast } from "sonner";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import BlockEditor from "@/components/BlockEditor";
import MembershipBadge from "@/components/MembershipBadge";
import { uploadBlockEditorImages } from "@/lib/block-editor-image-upload";
import { renderMixedContent } from "@/lib/renderContentUtils";
import { getProductName } from "@/constants/pdf-products";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import {
  COMMUNITY_ISSUES_PATH,
  COMMUNITY_ISSUE_LIMITS,
  type CommunityIssue,
} from "@/lib/community-issues";
import {
  deleteCommunityIssue,
  updateOwnCommunityIssue,
} from "@/lib/server-actions/community-issues/issues";
import type { PassType } from "@/types/pass";
import { IssueCategoryBadge, IssueStatusBadge, formatIssueDate } from "../IssueBadges";

interface IssueDetailCardProps {
  issue: CommunityIssue;
}

const fieldClass =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-[#333] focus:outline-none focus:ring-2 focus:ring-amber-400 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100";

/**
 * 이슈 본문 카드. 서버가 넘긴 issue 를 로컬 state 로 들고, 본인/관리자에게만 수정·삭제를 노출한다.
 * 권한의 최종 판단은 server action(`updateOwnCommunityIssue`/`deleteCommunityIssue`)이 한다.
 */
export default function IssueDetailCard({ issue: initialIssue }: IssueDetailCardProps) {
  const router = useRouter();
  const { isSignedIn, user } = useUser();
  const { isAdmin } = useAdminStatus();
  const [issue, setIssue] = useState(initialIssue);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(initialIssue.title);
  const [content, setContent] = useState(initialIssue.content);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const [avatarError, setAvatarError] = useState(false);
  const imageFileMapRef = useRef<Map<string, File>>(new Map());
  const handleImageFilesChange = useCallback((nextMap: Map<string, File>) => {
    imageFileMapRef.current = nextMap;
  }, []);

  // Cache Components는 이탈한 페이지를 Activity로 숨긴다. 숨겨질 때 편집을 끝낸다.
  useLayoutEffect(() => {
    return () => {
      setEditing(false);
      setTitle("");
      setContent("");
      setError(null);
      imageFileMapRef.current.clear();
    };
  }, []);

  const isOwner = isSignedIn === true && user?.id === issue.clerkUserId;
  const canEdit = isOwner || isAdmin;
  const canDelete = isAdmin || (isOwner && issue.status === "open");

  const handleSave = () => {
    setError(null);
    const contentAtSubmit = content;
    const imageFileMapAtSubmit = new Map(imageFileMapRef.current);
    startTransition(async () => {
      try {
        const processedContent = await uploadBlockEditorImages({
          content: contentAtSubmit,
          imageFileMap: imageFileMapAtSubmit,
          uploadEndpoint: "/api/upload-comment-image",
        });
        const result = await updateOwnCommunityIssue(issue.id, {
          title: title.trim(),
          content: processedContent.trim(),
        });
        if (!result.success) {
          setError(result.error);
          return;
        }
        setIssue(result.issue);
        setContent(result.issue.content);
        setEditing(false);
        toast.success("글을 수정했어요.");
        router.refresh();
      } catch (saveError) {
        console.error("글 수정 실패:", saveError);
        setError(saveError instanceof Error ? saveError.message : "글 수정에 실패했습니다.");
      }
    });
  };

  const handleDelete = () => {
    if (!window.confirm("이 글을 삭제할까요? 삭제하면 목록에서 사라져요.")) return;
    startTransition(async () => {
      const result = await deleteCommunityIssue(issue.id);
      if (!result.success) {
        toast.error(result.error);
        return;
      }
      toast.success("글을 삭제했어요.");
      router.push(COMMUNITY_ISSUES_PATH);
      router.refresh();
    });
  };

  return (
    <article className="mt-3 rounded-md border border-gray-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900/40 sm:p-5">
      {editing ? (
        <div className="space-y-3">
          <input
            type="text"
            value={title}
            maxLength={COMMUNITY_ISSUE_LIMITS.title}
            onChange={(e) => setTitle(e.target.value)}
            aria-label="제목"
            className={`text-lg font-semibold ${fieldClass}`}
          />
          <div className="rounded-md border border-gray-200 bg-white p-2 dark:border-neutral-600 dark:bg-neutral-800">
            <BlockEditor
              value={content}
              onChange={setContent}
              placeholder="내용"
              onImageFilesChange={handleImageFilesChange}
              maxImages={COMMUNITY_ISSUE_LIMITS.images}
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setTitle(issue.title);
                setContent(issue.content);
                setError(null);
                imageFileMapRef.current.clear();
              }}
              disabled={isPending}
              className="rounded-md px-3 py-1.5 text-sm text-[#706E6E] hover:bg-gray-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isPending || title.trim().length === 0 || content.trim().length === 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60 dark:bg-amber-400 dark:text-amber-950 dark:hover:bg-amber-300"
            >
              {isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
              저장
            </button>
          </div>
        </div>
      ) : (
        <>
          <header>
            <h1 className="text-xl font-semibold leading-snug text-[#333] dark:text-neutral-100 sm:text-2xl">
              {issue.title}{" "}
              <span className="font-normal text-[#706E6E] dark:text-neutral-500">#{issue.id}</span>
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <IssueStatusBadge status={issue.status} />
              <IssueCategoryBadge category={issue.category} />
              {issue.productId && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-neutral-800 dark:text-neutral-300">
                  {getProductName(issue.productId)}
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-neutral-300">
              <Image
                src={issue.profileImageUrl && !avatarError ? issue.profileImageUrl : "/default_gamja.webp"}
                alt=""
                width={24}
                height={24}
                className="size-6 rounded-full object-cover"
                onError={() => setAvatarError(true)}
              />
              <span className="font-medium">{issue.author ?? "익명"}</span>
              <MembershipBadge passType={(issue.passType as PassType | null) ?? null} />
              <time
                dateTime={issue.createdAt}
                className="text-xs text-[#706E6E] dark:text-neutral-400"
              >
                {formatIssueDate(issue.createdAt, true)}
              </time>
              {issue.contentEditedAt && (
                <span className="text-xs text-[#706E6E] dark:text-neutral-500">(수정됨)</span>
              )}
            </div>
          </header>

          <div className="mt-4 break-words text-[15px] leading-relaxed text-[#333] dark:text-neutral-200">
            {renderMixedContent(issue.content, { componentId: `issue-${issue.id}` })}
          </div>

          {issue.imageUrls.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {issue.imageUrls.map((url, index) => (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Image
                    src={url}
                    alt={`첨부 이미지 ${index + 1}`}
                    width={240}
                    height={240}
                    className="max-h-[240px] max-w-[240px] rounded-md border border-gray-200 object-cover transition-opacity hover:opacity-80 dark:border-neutral-600"
                  />
                </a>
              ))}
            </div>
          )}

          {(canEdit || canDelete) && (
            <div className="mt-4 flex justify-end gap-1 border-t border-gray-100 pt-3 dark:border-neutral-800">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    setTitle(issue.title);
                    setContent(issue.content);
                    setError(null);
                    imageFileMapRef.current.clear();
                    setEditing(true);
                  }}
                  disabled={isPending}
                  className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-[#706E6E] hover:bg-gray-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                >
                  <Pencil className="size-3.5" aria-hidden />
                  수정
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isPending}
                  className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                >
                  <Trash2 className="size-3.5" aria-hidden />
                  삭제
                </button>
              )}
            </div>
          )}
        </>
      )}
    </article>
  );
}
